import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import {
  CheckCircle2,
  Circle,
  Clock,
  Loader2,
  AlertTriangle,
  ListChecks,
} from 'lucide-react';
import type { PhaseSummary } from '@/shared/workflowAITypes';

interface PhaseTrackerProps {
  phases: PhaseSummary[];
  activePhaseId: string | null;
}

const statusConfig: Record<
  PhaseSummary['status'],
  { icon: React.ElementType; color: string; bgColor: string; label: string }
> = {
  pending:            { icon: Circle,         color: 'text-slate-400',    bgColor: 'bg-slate-100',       label: 'Pending' },
  active:             { icon: Loader2,        color: 'text-workfinity-600', bgColor: 'bg-workfinity-100', label: 'Active' },
  awaiting_answers:   { icon: Clock,          color: 'text-amber-600',    bgColor: 'bg-amber-100',       label: 'Awaiting' },
  awaiting_selection: { icon: ListChecks,     color: 'text-violet-600',   bgColor: 'bg-violet-100',      label: 'Choose' },
  completed:          { icon: CheckCircle2,   color: 'text-emerald-600',  bgColor: 'bg-emerald-100',     label: 'Done' },
  stale:              { icon: AlertTriangle,  color: 'text-amber-600',    bgColor: 'bg-amber-100',       label: 'Stale' },
};

export function PhaseTracker({ phases, activePhaseId }: PhaseTrackerProps) {
  if (phases.length === 0) return null;

  return (
    <div className="space-y-1 w-full min-w-0">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 px-1 mb-3">
        Workflow Phases
      </h3>

      <div className="space-y-0">
        {phases.map((phase, idx) => {
          const cfg = statusConfig[phase.status];
          const Icon = cfg.icon;
          const isActive = phase.id === activePhaseId;
          const isLast = idx === phases.length - 1;

          return (
            <motion.div
              key={phase.id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.25, delay: idx * 0.06 }}
              className="flex items-stretch"
            >
              {/* Timeline column */}
              <div className="flex flex-col items-center mr-3 relative">
                {/* Icon circle */}
                <div
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 z-10',
                    isActive ? 'ring-2 ring-workfinity-300 ring-offset-2' : '',
                    cfg.bgColor,
                  )}
                >
                  <Icon
                    className={cn(
                      'h-4 w-4',
                      cfg.color,
                      phase.status === 'active' && 'animate-spin',
                    )}
                  />
                </div>

                {/* Connector line */}
                {!isLast && (
                  <div
                    className={cn(
                      'w-0.5 flex-1 min-h-[20px]',
                      phase.status === 'completed'
                        ? 'bg-gradient-to-b from-emerald-300 to-slate-200'
                        : 'bg-slate-200',
                    )}
                  />
                )}
              </div>

              {/* Content */}
              <div
                className={cn(
                  'flex-1 min-w-0 pb-4 pt-1',
                  !isLast && 'border-b-0',
                )}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      'text-sm font-semibold truncate',
                      isActive ? 'text-workfinity-700' : 'text-slate-700',
                    )}
                  >
                    {phase.name}
                  </span>
                  {phase.version > 0 && (
                    <span className="text-[10px] text-slate-400 font-mono">
                      v{phase.version}
                    </span>
                  )}
                </div>

                <span
                  className={cn(
                    'inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium',
                    cfg.bgColor,
                    cfg.color,
                  )}
                >
                  {cfg.label}
                </span>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
